import { useState, useEffect, Fragment } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import logoHorizontal from '@assets/images/logo/logo-horizontal.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle body scroll when mobile menu is opened/closed
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Prevent scrolling on the body when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when menu is closed
      document.body.style.overflow = '';
    }

    // Cleanup function to ensure we re-enable scrolling if component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking on a link
  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 py-2 ${
        isScrolled
          ? 'bg-white shadow-md bg-white py-0'
          : isMobileMenuOpen
          ? 'bg-white shadow-md bg-white py-0'
          : 'bg-transparent backdrop-blur-sm py-0'
      }`}
    >
      <div className='container-custom'>
        <div className='flex justify-between items-center'>
          {/* Logo */}
          <div className='flex-shrink-0'>
            <Link to='/' className='group'>
              <div className='rounded-xl p-2 transition-all duration-300 transform group-hover:scale-105'>
                <img
                  src={logoHorizontal}
                  alt='Instituto Creciendo'
                  className='h-12 md:h-14'
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className='hidden md:flex space-x-1'>
            <NavLink
              to='/'
              className={({ isActive }) =>
                `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isActive ? 'font-bold' : 'text-gray-700 hover:text-gray-500'
                }`
              }
            >
              Inicio
            </NavLink>
            <NavLink
              to='/servicios'
              className={({ isActive }) =>
                `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isActive ? 'font-bold' : 'text-gray-700 hover:text-gray-500'
                }`
              }
            >
              Servicios
            </NavLink>
            <NavLink
              to='/nosotros'
              className={({ isActive }) =>
                `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isActive ? 'font-bold' : 'text-gray-700 hover:text-gray-500'
                }`
              }
            >
              Nosotros
            </NavLink>
            <NavLink
              to='/contacto'
              className='btn btn-secondary'
              style={{ padding: '0px 18px' }}
            >
              Contáctanos
            </NavLink>
          </div>

          {/* Mobile menu button */}
          <div className='md:hidden'>
            <button
              className='p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary'
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-label='Toggle navigation menu'
            >
              {isMobileMenuOpen ? (
                <XMarkIcon
                  className='h-6 w-6 text-gray-700'
                  aria-hidden='true'
                />
              ) : (
                <Bars3Icon
                  className='h-6 w-6 text-gray-700'
                  aria-hidden='true'
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Full-screen mobile menu - slides from header bottom */}
      <Transition
        show={isMobileMenuOpen}
        as={Fragment}
        enter='transition ease-out duration-300'
        enterFrom='transform origin-top scale-y-0 opacity-0'
        enterTo='transform origin-top scale-y-100 opacity-100'
        leave='transition ease-in duration-200'
        leaveFrom='transform origin-top scale-y-100 opacity-100'
        leaveTo='transform origin-top scale-y-0 opacity-0'
      >
        <div
          className={`absolute top-full left-0 w-full ${
            isScrolled ? 'bg-white' : 'bg-white backdrop-blur-sm'
          } shadow-lg z-40`}
        >
          <div className='flex flex-col py-6 px-6 h-[calc(100vh-80px)]'>
            <NavLink
              to='/'
              onClick={handleMobileNavClick}
              className={({ isActive }) =>
                `px-4 py-5 my-1 rounded-lg font-medium text-lg transition-all ${
                  isActive
                    ? 'bg-primary-cold/10 text-primary-cold font-bold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              Inicio
            </NavLink>
            <NavLink
              to='/servicios'
              onClick={handleMobileNavClick}
              className={({ isActive }) =>
                `px-4 py-5 my-1 rounded-lg font-medium text-lg transition-all ${
                  isActive
                    ? 'bg-primary-cold/10 text-primary-cold font-bold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              Servicios
            </NavLink>
            <NavLink
              to='/nosotros'
              onClick={handleMobileNavClick}
              className={({ isActive }) =>
                `px-4 py-5 my-1 rounded-lg font-medium text-lg transition-all ${
                  isActive
                    ? 'bg-primary-cold/10 text-primary-cold font-bold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              Nosotros
            </NavLink>
            <div className='mt-auto'>
              <NavLink
                to='/contacto'
                onClick={handleMobileNavClick}
                className='btn btn-secondary w-full flex justify-center text-lg py-4'
              >
                Contáctanos
              </NavLink>
            </div>
          </div>
        </div>
      </Transition>
    </header>
  );
};

export default Header;
